import React from 'react';
import { colors } from '@/assets/styles/colors';
import { Spinner } from '@/components/ui/loading';
import { TABLE_PALETTE } from './Table.constants';


// Fila alternada por índice (0-based): filas impares (1ª, 3ª...) blancas, pares #F8F8F8.
// No hay token en colors.ts para #F8F8F8 — se usa el literal del diseño directamente.
const EVEN_ROW_BACKGROUND = '#F8F8F8';

export interface SimpleScrollTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
}

export interface SimpleScrollTableProps<T> {
  columns: SimpleScrollTableColumn<T>[];
  data: T[];
  getRowId: (row: T) => string | number;
  /** Muestra una fila final con spinner (paginación por scroll infinito en curso). */
  isLoadingMore?: boolean;
  /**
   * Callback ref (no `useRef` object) para el contenedor con scroll: permite adjuntar un
   * listener de scroll incluso si este componente aparece después de que el efecto que lo
   * adjunta ya corrió una vez (ver, p. ej., los modales de aprobación/rechazo masivo).
   */
  scrollContainerRef?: (node: HTMLDivElement | null) => void;
  /** Clases adicionales para el contenedor con scroll (p. ej. `flex-1` dentro de un panel). */
  className?: string;
  'data-testid'?: string;
}

/**
 * Tabla compacta con scroll infinito para listados fijos de pocas columnas dentro de un
 * panel acotado (p. ej. el listado de solicitudes dentro de un modal de acción masiva). A
 * diferencia de `Table`, no incluye buscador, paginación, orden ni selección — cuando se
 * necesite alguno de esos comportamientos, usar `Table` en su lugar.
 */
export function SimpleScrollTable<T>({
  columns,
  data,
  getRowId,
  isLoadingMore = false,
  scrollContainerRef,
  className = '',
  'data-testid': dataTestId,
}: SimpleScrollTableProps<T>) {
  return (
    <div
      ref={scrollContainerRef}
      className={`scrollbar-thin-dark overflow-y-auto ${className}`}
      data-testid={dataTestId}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b" style={{ borderColor: TABLE_PALETTE.border }}>
            {columns.map((column) => (
              <th key={column.key} className="text-left px-4 py-2 font-medium text-black">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr
              key={getRowId(row)}
              style={{
                backgroundColor: index % 2 === 0 ? colors.grays.neutralFF : EVEN_ROW_BACKGROUND,
              }}
            >
              {columns.map((column) => (
                <td key={column.key} className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
          {isLoadingMore && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-3 text-center">
                <div className="flex justify-center">
                  <Spinner size="sm" />
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default SimpleScrollTable;
